import { chatService } from "@/services/chat.service";
import { readChatArchive, type ChatConversation, type ChatTurn } from "./chat-model";

type Header = {
  id: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  revision: number;
  contentRevision: number;
  deleted?: boolean;
};
type RemoteTurn = ChatTurn & { revision: number; running?: boolean };
type Page = { items: RemoteTurn[]; conversation: Header; cursor?: string };
const fields = [
  "id",
  "query",
  "createdAt",
  "state",
  "text",
  "sources",
  "images",
  "answer",
  "objects",
  "help",
  "operations",
  "requestID",
  "requestStartedAt",
  "selectedReferences",
  "operationRequest",
  "operationTargetID",
  "openerReferences",
  "openerAsk",
  "textSettled",
  "error",
] as const;
export const chatTurnDocument = (turn: ChatTurn) =>
  Object.fromEntries(fields.filter(key => turn[key] !== undefined).map(key => [key, turn[key]]));
const ordered = (value: unknown): unknown =>
  Array.isArray(value)
    ? value.map(ordered)
    : value && typeof value === "object"
      ? Object.fromEntries(
          Object.keys(value)
            .sort()
            .map(key => [key, ordered((value as Record<string, unknown>)[key])]),
        )
      : value;
async function signature(turn: ChatTurn) {
  const bytes = new TextEncoder().encode(JSON.stringify(ordered(chatTurnDocument(turn))));
  return Array.from(new Uint8Array(await crypto.subtle.digest("SHA-256", bytes)), byte =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
}
const header = (conversation: Pick<ChatConversation, "id" | "title" | "createdAt">) => ({
  id: conversation.id,
  title: conversation.title,
  createdAt: conversation.createdAt,
});

/** Archive ownership stays in ChatStore. This exchanges documents, never drafts,
 * selected items, menu state, or duplicate copies in the query cache. */
export async function syncChatDocuments(
  local: ChatConversation[],
  deletions: string[],
  running: Set<string>,
  signal: AbortSignal,
): Promise<{ conversations: ChatConversation[]; deleted: string[]; notice?: string }> {
  const call = <T>(body: unknown) => chatService.history<T>(body, signal);
  const headers = new Map<string, Header>();
  let cursor: string | undefined;
  do {
    const page = await call<{ items: Header[]; cursor?: string }>({ action: "list", cursor });
    page.items.forEach(item => headers.set(item.id, item));
    cursor = page.cursor;
  } while (cursor);
  const removed = new Set<string>();
  for (const id of deletions) {
    await call({ action: "delete", id });
    removed.add(id);
  }
  for (const item of headers.values()) if (item.deleted) removed.add(item.id);
  const locals = new Map(local.map(item => [item.id, item]));
  const result: ChatConversation[] = [];
  let notice: string | undefined;
  for (const id of new Set([...locals.keys(), ...headers.keys()])) {
    signal.throwIfAborted();
    if (removed.has(id)) continue;
    const before = locals.get(id);
    let remote = headers.get(id);
    if (!remote && !before?.turns.length) {
      if (before) result.push(before);
      continue;
    }
    let turns = new Map<string, ChatTurn>();
    const unfinished = new Set<string>();
    let observedRevision = before?.cloud?.contentRevision ?? -1;
    if (
      remote &&
      (!before?.cloud ||
        before.cloud.contentRevision !== remote.contentRevision ||
        before.cloud.revision !== remote.revision ||
        before.turns.some(turn => turn.cloud?.running))
    ) {
      const received: RemoteTurn[] = [];
      do {
        const page = await call<Page>({ action: "get", id, cursor });
        if (!cursor || page.conversation.deleted) remote = page.conversation;
        received.push(...page.items);
        cursor = page.cursor;
      } while (cursor);
      if (remote.deleted) {
        removed.add(id);
        continue;
      }
      observedRevision = remote.contentRevision;
      for (const turn of received) if (turn.state === "answering") unfinished.add(turn.id);
      const parsed = readChatArchive(
        {
          version: 1,
          conversations: [
            {
              ...remote,
              draft: "",
              turns: received.map(turn =>
                turn.state === "answering" && !turn.running ? { ...turn, state: "interrupted" } : turn,
              ),
            },
          ],
        },
        false,
      ).conversations[0];
      for (const turn of parsed.turns) {
        const raw = received.find(item => item.id === turn.id)!;
        turns.set(turn.id, {
          ...turn,
          cloud: { revision: raw.revision, signature: await signature(turn), running: raw.running },
        });
      }
    } else turns = new Map((before?.turns || []).filter(turn => turn.cloud).map(turn => [turn.id, turn]));

    const uploads: { turn: Record<string, unknown>; revision: number }[] = [];
    for (const turn of before?.turns || []) {
      const other = turns.get(turn.id);
      // The local stream owns its live text. Its final checkpoint triggers sync.
      if (running.has(id) && turn.state === "answering" && !turn.operationRequest) {
        turns.set(turn.id, turn);
        continue;
      }
      // If the server's final write failed, keep the completed local checkpoint.
      // Wait for its writer lease, then publish against the observed revision.
      if (other && unfinished.has(turn.id) && turn.state === "complete") {
        turns.set(turn.id, { ...turn, cloud: other.cloud });
        if (!other.cloud?.running) uploads.push({ turn: chatTurnDocument(turn), revision: other.cloud!.revision });
        continue;
      }
      if (other?.cloud?.running) continue;
      if (other && (!turn.cloud || other.cloud!.revision !== turn.cloud.revision)) continue;
      if (!other || (turn.cloud && (await signature(turn)) !== turn.cloud.signature))
        uploads.push({ turn: chatTurnDocument(turn), revision: turn.cloud?.revision || 0 });
      turns.set(turn.id, turn);
    }
    for (let at = 0; at < uploads.length; at += 20) {
      const page = await call<Page>({
        action: "put",
        conversation: header(before!),
        turns: uploads.slice(at, at + 20),
      });
      remote = page.conversation;
      for (const uploaded of page.items) {
        const original = turns.get(uploaded.id)!;
        turns.set(uploaded.id, {
          ...original,
          cloud: { revision: uploaded.revision, signature: await signature(original) },
        });
      }
    }
    // A live first turn may already have created its remote header after list().
    if (!remote) {
      if (before) result.push(before);
      continue;
    }
    const renamed = before && before.title !== (before.cloud?.title ?? remote.title);
    if (renamed && before.titleWasEdited) {
      if (before.cloud && before.cloud.revision !== remote.revision) {
        notice = "A chat was renamed on another device. Its latest name is shown; you can rename it again.";
      } else {
        const response = await call<{ conversation: Header }>({
          action: "rename",
          id,
          title: before.title,
          revision: remote.revision,
        });
        remote = response.conversation;
      }
    }
    result.push({
      ...before,
      ...header(remote),
      title: remote.title,
      updatedAt: Math.max(before?.updatedAt || 0, remote.updatedAt),
      draft: before?.draft || "",
      turns: [...turns.values()].sort((a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id)),
      cloud: {
        revision: remote.revision,
        contentRevision: uploads.length ? -1 : observedRevision,
        title: remote.title,
      },
    });
  }
  return { conversations: result, deleted: [...removed], notice };
}
